import { loc } from '../data/cards'
import { useEffect, useMemo, useState } from 'react'
import { useMainButton } from '../components/MainButton'
import { NotifyOffer, canOfferNotify } from '../components/NotifyOffer'
import { RewardModal } from '../components/RewardModal'
import { api } from '../lib/api'
import { takeLogin } from '../lib/auth'
import { TarotCard } from '../components/TarotCard'
import { cardById, cardMeaning, cardName, drawCards } from '../data/cards'
import { SPREADS, spreadName } from '../data/spreads'
import type { SpreadId } from '../data/spreads'
import { formatById, formatDesc, formatName, type FormatId } from '../data/formats'
import { ThemeChips } from '../components/ThemeChips'
import { greetingKey } from '../i18n'
import { haptic, telegramUser } from '../lib/telegram'
import { useSirr } from '../store'

interface Props {
  onOpenCatalog: () => void
  onOpenStars: () => void
  onStartSpread: (id: SpreadId) => void
  onOpenFormat: (id: FormatId) => void
  /** Экран входа: туда ведёт предложение анониму на карте дня. */
  onLogin: () => void
}

/** Крючки на главной: кофе и джинн — то, ради чего приходят впервые. */
const HOOKS: FormatId[] = ['coffee', 'djinn']

export function Home({ onOpenCatalog, onOpenStars, onStartSpread, onOpenFormat, onLogin }: Props) {
  const { profile, lang, t, today, remaining, update, anon, freeFortune } = useSirr()

  const user = telegramUser()
  // Имя: в Telegram — из Telegram, в браузере — с сервера (Google/почта), иначе «Гость».
  const name = user?.first_name || profile.name || t('guest')

  // Карта дня привязана к пользователю и дате — одна и та же до полуночи.
  const day = useMemo(() => drawCards(`${user?.id ?? 'guest'}-${today}-day`, 1)[0], [user?.id, today])
  const dayCard = cardById(day.id)
  // Подпись к перевёрнутой карте и само значение — одинаковы для всех языков
  // фразы ниже, поэтому считаем их один раз.
  const rev = day.reversed ? ` (${t('reversed')})` : ''
  const dayMeaning = cardMeaning(dayCard, lang, day.reversed)
  const flipped = Boolean(profile.dayFlipped[today])

  /*
    Предложение включить напоминания — при каждом открытии карты дня, пока они
    не включены. Так решил владелец; правило записано в `NotifyOffer`, вместе с
    единственным случаем, когда окна не будет (человек уже отказал системному
    запросу — там кнопка физически не работает).

    Момент выбран не случайно: карта дня — это ровно то, про что мы и
    напоминаем. Предлагать раньше значит объяснять ценность словами вместо
    того, чтобы показать её.
  */
  const [offerNotify, setOfferNotify] = useState(false)
  const [notifyGranted, setNotifyGranted] = useState(0)
  // Суммы берём у сервера: они настраиваются переменными окружения, и зашитые в
  // текст разъехались бы с начисляемыми — обещали бы одно, давали другое.
  const [notifyBonus, setNotifyBonus] = useState(0)
  useEffect(() => {
    if (profile.notify) return
    // Анониму окно показывается ради входа, поэтому запрет системного запроса
    // его не отменяет: за вход мы всё равно начислим, просто не пообещаем
    // вторую половину.
    if (!anon && !canOfferNotify()) return
    api
      .me()
      .then((r) => {
        setNotifyBonus(canOfferNotify() ? (r.ads?.notify ?? 0) : 0)
      })
      .catch(() => {})
  }, [profile.notify, anon])

  /*
    Только что вошли — говорим про начисленное, а закрыв это окно, человек
    видит предложение про напоминания. Цепочка и есть смысл всей затеи: вход
    был первой половиной подарка, и вторая обязана прийти сразу, а не завтра
    при следующем открытии карты дня.
  */
  const [loginGranted, setLoginGranted] = useState<{ shown: boolean; stars: number }>({ shown: false, stars: 0 })
  useEffect(() => {
    // По факту входа, а не по звёздам: награда обнулена, а подтвердить
    // подарок всё равно надо.
    const { justLoggedIn, stars } = takeLogin()
    if (justLoggedIn) setLoginGranted({ shown: true, stars })
  }, [])

  const flip = () => {
    if (flipped) return
    haptic.notify('success')
    update((p) => ({ dayFlipped: { ...p.dayFlipped, [today]: true } }))
    if (profile.notify) return
    // Анониму — всегда: предложение войти держится не на звёздах (за вход их
    // больше не дают), а на даровом первом гадании, которого у него не было.
    if (anon || canOfferNotify()) setOfferNotify(true)
  }

  useMainButton({
    label: flipped ? t('makeSpread') : t('openDayCard'),
    onClick: flipped ? onOpenCatalog : flip,
  })

  return (
    <div className="screen">
      {offerNotify && (
        <NotifyOffer
          bonus={notifyBonus}
          // Предложение войти — только анониму. Условие по `anon`, а не по
          // одному лишь числу: после входа профиль перечитывается, и стрелять
          // старым значением в уже вошедшего нельзя.
          toLogin={anon}
          onLogin={onLogin}
          onClose={() => setOfferNotify(false)}
          onGranted={(stars, balance) => {
            setOfferNotify(false)
            if (balance !== undefined) update({ stars: balance })
            if (stars > 0) setNotifyGranted(stars)
          }}
        />
      )}
      {/* Награда пришла — говорим тем же окном, что и после ролика. */}
      <RewardModal
        stars={notifyGranted}
        title={t('notifyBonusDone')}
        body={t('notifyBonusDoneBody', { n: notifyGranted })}
        ok={t('rewardOk')}
        onClose={() => setNotifyGranted(0)}
      />
      {/* Награда за вход — и сразу вторая половина предложения. */}
      <RewardModal
        visible={loginGranted.shown && (loginGranted.stars > 0 || freeFortune)}
        stars={loginGranted.stars}
        title={t('loginBonusDone')}
        /* Подтверждаем подарок там, где его ждут: войти человека позвали
           обещанием бесплатного гадания. */
        body={
          loginGranted.stars > 0
            ? t(freeFortune ? 'loginBonusFree' : 'loginBonusDoneBody', { n: loginGranted.stars })
            : t('loginFreeOnly')
        }
        ok={t('rewardOk')}
        onClose={() => {
          setLoginGranted({ shown: false, stars: 0 })
          if (!profile.notify && notifyBonus > 0 && canOfferNotify()) setOfferNotify(true)
        }}
      />
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
            <div>
              <div style={{ fontFamily: 'var(--serif-body)', fontSize: 16, color: 'var(--ink-muted)' }}>
                {t(greetingKey())}
              </div>
              <div style={{ fontFamily: 'var(--serif)', fontStyle: 'italic', fontSize: 22 }}>{name}</div>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <Chip sym="✦" symColor="var(--gold)" value={profile.stars} />
              <Chip sym="♦" symColor="#e07a54" value={profile.streak} />
            </div>
          </div>

          {/* ── Тема чтения ───────────────────────────────────────── */}
          <ThemeChips />

          {/* ── Карта дня ─────────────────────────────────────────────
              Без панели с рамкой: карта дня — единственное, ради чего
              экран открывают, и коробка вокруг ставит её в один ряд с
              остальными блоками. Вместо рамки — ореол и дышащее кольцо. */}
          <section className="dayhalo">
            <div className="dayhalo__glow" />
            <div className="dayhalo__ring" />

            <div className="eyebrow" style={{ position: 'relative', marginBottom: 14 }}>
              {t('cardOfDay')}
            </div>

            <div style={{ position: 'relative', display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
              <button
                type="button"
                onClick={flip}
                aria-label={flipped ? cardName(dayCard, lang) : t('tapReveal')}
                style={{ perspective: '1100px', width: 168, height: 270 }}
              >
                <div
                  style={{
                    position: 'relative',
                    width: '100%',
                    height: '100%',
                    transformStyle: 'preserve-3d',
                    transition: 'transform 1s cubic-bezier(.4,0,.2,1)',
                    transform: `rotateY(${flipped ? 180 : 0}deg)`,
                  }}
                >
                  {/* рубашка */}
                  <div
                    style={{
                      position: 'absolute',
                      inset: 0,
                      backfaceVisibility: 'hidden',
                      borderRadius: 14,
                      overflow: 'hidden',
                      background: 'radial-gradient(circle at 50% 45%, rgba(58,30,92,.8), rgba(18,10,31,.95))',
                      border: '1px solid rgba(200,162,78,.5)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: 'var(--gold-glow)',
                    }}
                  >
                    <div
                      style={{ position: 'absolute', inset: 8, border: '1px solid rgba(200,162,78,.2)', borderRadius: 9 }}
                    />
                    <div
                      style={{
                        width: 44,
                        height: 44,
                        border: '1px solid rgba(200,162,78,.65)',
                        transform: 'rotate(45deg)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        animation: 'mpulse 3.4s ease-in-out infinite',
                      }}
                    >
                      <span style={{ transform: 'rotate(-45deg)', fontSize: 22, color: 'var(--gold)' }}>✦</span>
                    </div>
                    <div
                      style={{
                        position: 'absolute',
                        bottom: 14,
                        left: 0,
                        right: 0,
                        fontFamily: 'var(--serif-body)',
                        fontSize: 13,
                        color: 'var(--ink-muted)',
                      }}
                    >
                      {t('tapReveal')}
                    </div>
                  </div>

                  {/* лицо */}
                  <div
                    style={{
                      position: 'absolute',
                      inset: 0,
                      backfaceVisibility: 'hidden',
                      transform: 'rotateY(180deg)',
                    }}
                  >
                    <TarotCard
                      faceUp
                      cardId={dayCard.id}
                      sym={dayCard.sym}
                      rank={dayCard.rank}
                      title={cardName(dayCard, lang)}
                      reversed={day.reversed}
                      art="ornament"
                    />
                  </div>
                </div>
              </button>
            </div>

            {flipped ? (
              <p className="prose" style={{ position: 'relative', fontSize: 16, animation: 'fu .7s both' }}>
                {loc(
                  {
                    ru: `Сегодня ${cardName(dayCard, lang)}${rev} освещает ваш путь: ${dayMeaning}.`,
                    az: `Bu gün ${cardName(dayCard, lang)}${rev} yolunuzu işıqlandırır: ${dayMeaning}.`,
                    en: `Today ${cardName(dayCard, lang)}${rev} lights your way: ${dayMeaning}.`,
                    tr: `Bugün ${cardName(dayCard, lang)}${rev} yolunuzu aydınlatıyor: ${dayMeaning}.`,
                  },
                  lang,
                )}
              </p>
            ) : (
              <p className="prose" style={{ position: 'relative', fontSize: 16, color: 'var(--ink-muted)' }}>
                {t('dayHint')}
              </p>
            )}
          </section>

          {/* ── Крючки ───────────────────────────────────────────────
              Кофе и джинн стоят выше раскладов намеренно: за таро приходят
              вернувшиеся, а первые три секунды после запуска принадлежат
              тому, ради чего человек скачал приложение впервые. */}
          <div style={{ fontFamily: 'var(--serif)', fontStyle: 'italic', fontSize: 19, margin: '32px 0 12px' }}>
            {t('tryNew')}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 11 }}>
            {HOOKS.map((id) => formatById(id)).map(
              (f) =>
                f && (
                  <button key={f.id} type="button" className="hook" onClick={() => onOpenFormat(f.id)}>
                    <span className="hook__badge">{t('newBadge')}</span>
                    <span className="hook__sym">{f.sym}</span>
                    <span className="hook__name">{formatName(f, lang)}</span>
                    <span className="hook__desc">{formatDesc(f, lang)}</span>
                  </button>
                ),
            )}
          </div>

          {/* ── Быстрые расклады ──────────────────────────────────── */}
          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', margin: '24px 0 12px' }}>
            <div style={{ fontFamily: 'var(--serif)', fontStyle: 'italic', fontSize: 19 }}>{t('quickSpreads')}</div>
            <button type="button" style={{ fontSize: 12, color: 'var(--gold)' }} onClick={onOpenCatalog}>
              {t('all')} ›
            </button>
          </div>

          <div style={{ display: 'flex', gap: 11, overflowX: 'auto', paddingBottom: 4 }}>
            {SPREADS.slice(0, 4).map((sp) => (
              <button
                key={sp.id}
                type="button"
                className="pick"
                style={{ flex: '0 0 128px', padding: 14 }}
                onClick={() => onStartSpread(sp.id)}
              >
                <div
                  style={{
                    width: 34,
                    height: 34,
                    borderRadius: 9,
                    border: '1px solid rgba(200,162,78,.35)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 17,
                    color: 'var(--gold)',
                    marginBottom: 10,
                  }}
                >
                  {sp.sym}
                </div>
                <div style={{ fontFamily: 'var(--serif)', fontStyle: 'italic', fontSize: 15, lineHeight: 1.1 }}>
                  {spreadName(sp, lang)}
                </div>
                <div style={{ fontSize: 11, color: 'var(--ink-muted)', marginTop: 4 }}>
                  {sp.cost > 0 ? `✦ ${sp.cost}` : t('free')}
                </div>
              </button>
            ))}
          </div>

          {/* ── Premium ───────────────────────────────────────────── */}
          <button
            type="button"
            onClick={onOpenStars}
            style={{
              width: '100%',
              marginTop: 20,
              border: '1px solid rgba(200,162,78,.35)',
              borderRadius: 16,
              padding: '15px 16px',
              display: 'flex',
              alignItems: 'center',
              gap: 13,
              textAlign: 'left',
              background: 'linear-gradient(120deg, rgba(74,44,20,.35), rgba(45,26,74,.35))',
            }}
          >
            <div style={{ fontSize: 22, color: 'var(--gold)' }}>♛</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontFamily: 'var(--serif)', fontStyle: 'italic', fontSize: 16 }}>{t('premiumTitle')}</div>
              <div style={{ fontSize: 11.5, color: 'var(--ink-muted)' }}>{t('premiumSub')}</div>
            </div>
            <div style={{ fontSize: 18, color: 'var(--gold)' }}>›</div>
          </button>

      <p style={{ textAlign: 'center', fontSize: 11.5, color: 'var(--ink-faint)', marginTop: 14 }}>
        {t('limitLeft', { n: remaining })}
      </p>
    </div>
  )
}

function Chip({ sym, symColor, value }: { sym: string; symColor: string; value: number }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 4,
        border: '1px solid rgba(200,162,78,.3)',
        borderRadius: 20,
        padding: '5px 10px',
        fontSize: 12,
      }}
    >
      <span style={{ color: symColor }}>{sym}</span>
      {value}
    </div>
  )
}
